import { useState } from 'react';
import { TextInput, TextInputProps, StyleSheet, View, Text, Pressable } from 'react-native';
import { forwardRef } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';

export interface PasswordInputProps extends Omit<TextInputProps, 'secureTextEntry'> {
  label?: string;
  error?: string;
  hint?: string;
}

export const PasswordInput = forwardRef<TextInput, PasswordInputProps>(
  ({ label, error, hint, style, placeholderTextColor, ...props }, ref) => {
    const { tokens } = useTheme();
    const utils = createStyles(tokens);
    const styles = makeStyles(tokens, utils);
    const [isVisible, setIsVisible] = useState(false);

    // Calculate opacity for placeholder (80% = 0.8)
    const placeholderColor = placeholderTextColor || `${tokens.color.onSurface}CC`; // CC = 80% opacity in hex

    return (
      <View style={styles.container}>
        {label && <Text style={styles.label}>{label}</Text>}
        <View style={styles.inputContainer}>
          <TextInput
            ref={ref}
            style={[styles.input, error ? styles.inputError : null, style] as any}
            placeholderTextColor={placeholderColor}
            secureTextEntry={!isVisible}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="password"
            textContentType="password"
            spellCheck={false}
            {...props}
          />
          <Pressable
            onPress={() => setIsVisible(!isVisible)}
            style={styles.eyeButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityLabel={isVisible ? 'Hide password' : 'Show password'}
            accessibilityRole="button"
          >
            <Ionicons 
              name={isVisible ? 'eye-off' : 'eye'} 
              size={20} 
              color={tokens.color.onSurface}
              style={styles.eyeIcon}
            />
          </Pressable>
        </View>
        {error && <Text style={styles.errorText}>{error}</Text>}
        {hint && !error && <Text style={styles.hintText}>{hint}</Text>}
      </View>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      width: '100%',
      marginBottom: utils.spacing.md,
    },
    label: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    inputContainer: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
    },
    input: {
      flex: 1,
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.md,
      paddingRight: 48, // Space for eye icon
      paddingVertical: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
    },
    inputError: {
      borderColor: tokens.color.error,
    },
    eyeButton: {
      position: 'absolute',
      right: utils.spacing.md,
      padding: utils.spacing.xs,
      zIndex: 1,
    },
    eyeIcon: {
      opacity: 0.7,
    },
    errorText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.error,
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.semibold,
    },
    hintText: {
      fontSize: utils.typography.sizes.sm,
      color: `${tokens.color.onSurface}99`, // 99 = 60% opacity in hex
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}
